import { useCallback, useContext, useState } from "react";
import { api } from "../../../api";
import { OverflowFrame } from "../../../externalComponents";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { Divider } from "../../../externalComponents/tailwind/divider";
import {
  Description,
  Field,
  Label,
  Legend,
} from "../../../externalComponents/tailwind/fieldset";
import { Input, InputGroup } from "../../../externalComponents/tailwind/input";
import { Select } from "../../../externalComponents/tailwind/select";
import SelectInput from "../../../externalComponents/tailwind/select-input";
import {
  SwitchField,
  SwitchOnOff,
} from "../../../externalComponents/tailwind/switch";
import { Text } from "../../../externalComponents/tailwind/text";
import { CustomerContext } from "../../../providers";

export default function MessagesPage() {
  const { customer } = useContext(CustomerContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [shouldPostponeMessages, setShouldPostponeMessages] = useState(
    customer.shouldPostponeMessages ?? false,
  );
  const [shouldPreventAutomations, setShouldPreventAutomations] = useState(
    customer.shouldPreventAutomations ?? false,
  );
  const [
    selectedDelayLimitMarketingPressure,
    setSelectedDelayLimitMarketingPressure,
  ] = useState(customer.limitMarketingPressure ?? 2);
  const optionDelayLimitMarketingPressure = Array.from(
    { length: 30 },
    (_, i) => ({ label: `${i + 1} day${i > 0 ? "s" : ""}`, value: i + 1 }),
  );
  const [automaticUtm, setAutomaticUtm] = useState(customer.automaticUtm);
  const defaultAutomaticUtm = [
    {
      label: "utm_source",
      value: "crm",
    },
    {
      label: "utm_medium",
      value: "whatsapp_simio",
    },
    {
      label: "utm_campaign",
      value: "flow_name",
    },
  ];

  const [isAutomaticUtmActivated, setIsAutomaticUtmActivated] = useState(
    customer.isAutomaticUtmActivated ?? false,
  );
  const utmCampaignOptions = [
    { label: "Flow name", value: "flow_name" },
    { label: "Flow id", value: "flow_id" },
    { label: "Flow name / ID", value: "flow_name_and_id" },
  ];
  const [utmSource, setUtmSource] = useState(
    automaticUtm?.find((item) => item.label === "utm_source")?.value || "crm",
  );
  const [utmMedium, setUtmMedium] = useState(
    automaticUtm?.find((item) => item.label === "utm_medium")?.value ||
      "whatsapp_simio",
  );
  const [utmCampaign, setUtmCampaign] = useState(
    automaticUtm?.find((item) => item.label === "utm_campaign")?.value ||
      utmCampaignOptions[0].value,
  );

  const handleDaysSelectedChange = useCallback(async (value) => {
    const intValue = parseInt(value, 10);
    setSelectedDelayLimitMarketingPressure(intValue);
    await api.customer.update(customer.id, {
      limitMarketingPressure: intValue,
    });
  }, []);

  const handleActivateAutomaticUtm = async (checked) => {
    setIsAutomaticUtmActivated(checked);
    await api.customer.update(customer.id, {
      isAutomaticUtmActivated: checked,
    });
    if (!automaticUtm) {
      setAutomaticUtm(defaultAutomaticUtm);
      await api.customer.update(customer.id, {
        automaticUtm: defaultAutomaticUtm,
      });
    }
  };

  const handleUpdateAutomaticUtm = async ({ utmValue, utmLabel }) => {
    setIsEditing(true);
    const updatedAutomaticUtm = automaticUtm.map((item) =>
      item.label === utmLabel ? { ...item, value: utmValue } : item,
    );
    setAutomaticUtm(updatedAutomaticUtm);
  };

  const saveGeneralSettings = async () => {
    setIsLoading(true);

    try {
      await api.customer.update(customer.id, {
        automaticUtm: automaticUtm,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
      setIsEditing(false);
    }
  };

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="Messages"
          primaryAction="Save"
          handlePrimaryAction={saveGeneralSettings}
          isPrimaryLoading={isLoading}
        />

        <OverflowFrame>
          <div className="my-5">
            {customer?.isSubscribed &&
              (customer?.channelId360Dialog || customer?.waAccessToken) && (
                <>
                  <Field>
                    <Legend>Sending rate limit per minute</Legend>

                    <Description>
                      Messages to be sent are limited to{" "}
                      {customer?.sendingRatePerMinute || 250} per minute.
                    </Description>
                  </Field>
                  <Divider />
                  <Field>
                    <Legend>Postpone late messages</Legend>
                    <SwitchField className="w-80">
                      <Label>Should postpone messages</Label>
                      <SwitchOnOff
                        name="activate_postpone_message"
                        checked={shouldPostponeMessages}
                        onChange={async (checked) => {
                          setShouldPostponeMessages(checked);
                          await api.customer.update(customer.id, {
                            shouldPostponeMessages: checked,
                          });
                        }}
                      ></SwitchOnOff>
                    </SwitchField>
                    <Description>
                      Messages to be sent after 9 p.m. will be sent the
                      following morning from 9 a.m. (Paris time).
                    </Description>
                  </Field>
                  <Divider />
                  <Field>
                    <Legend>Limit marketing pressure</Legend>
                    <SwitchField className="w-80">
                      <Label>Should prevent repeated sendings</Label>
                      <SwitchOnOff
                        name="activate_prevent_automations"
                        checked={shouldPreventAutomations}
                        onChange={async (checked) => {
                          setShouldPreventAutomations(checked);
                          await api.customer.update(customer.id, {
                            shouldPreventAutomations: checked,
                          });
                        }}
                      ></SwitchOnOff>
                    </SwitchField>
                    <Description>
                      Automation messages will not be sent if the contact has
                      already received a message in the last{" "}
                      {shouldPreventAutomations
                        ? selectedDelayLimitMarketingPressure
                        : "X"}{" "}
                      days.
                    </Description>
                    {shouldPreventAutomations && (
                      <Field className="flex items-center justify-between w-80">
                        <Label>Delay between messages</Label>
                        <div className="w-[100px] my-2">
                          <SelectInput
                            options={optionDelayLimitMarketingPressure}
                            value={selectedDelayLimitMarketingPressure}
                            selected={selectedDelayLimitMarketingPressure}
                            onChange={handleDaysSelectedChange}
                          />
                        </div>
                      </Field>
                    )}
                  </Field>
                  <Divider />
                  <Field>
                    <Legend>Automatic UTM</Legend>
                    <div className="flex content-center items-center mt-5">
                      <SwitchField className="w-80">
                        <Label>Automatically add UTM on links</Label>
                        <SwitchOnOff
                          name="activate_postpone_message"
                          checked={isAutomaticUtmActivated}
                          onChange={(checked) =>
                            handleActivateAutomaticUtm(checked)
                          }
                        ></SwitchOnOff>
                      </SwitchField>
                      {isEditing && (
                        <Text className="text-pink text-xs ml-3">
                          Unsaved settings changes
                        </Text>
                      )}
                    </div>
                    <div className="mt-2">
                      <Label>UTM Source</Label>
                      <InputGroup>
                        <Input
                          name="utm-source"
                          value={utmSource}
                          onChange={(e) => {
                            setUtmSource(e.target.value);
                            handleUpdateAutomaticUtm({
                              utmValue: e.target.value,
                              utmLabel: "utm_source",
                            });
                          }}
                          maxLength={32}
                          showCharacterCount
                          autoComplete="off"
                          disabled={!isAutomaticUtmActivated}
                        />
                      </InputGroup>
                    </div>
                    <div className="mt-2">
                      <Label>UTM Medium</Label>
                      <InputGroup>
                        <Input
                          name="utm-medium"
                          value={utmMedium}
                          onChange={(e) => {
                            setUtmMedium(e.target.value);
                            handleUpdateAutomaticUtm({
                              utmValue: e.target.value,
                              utmLabel: "utm_medium",
                            });
                          }}
                          maxLength={32}
                          showCharacterCount
                          autoComplete="off"
                          disabled={!isAutomaticUtmActivated}
                        />
                      </InputGroup>
                    </div>
                    <div className="mt-2">
                      <Label className="mt-2">UTM Campaign</Label>
                      <Select
                        key={"utm-campaign"}
                        disabled={!isAutomaticUtmActivated}
                        onChange={(e) => {
                          setUtmCampaign(e.target.value);
                          handleUpdateAutomaticUtm({
                            utmValue: e.target.value,
                            utmLabel: "utm_campaign",
                          });
                        }}
                        value={utmCampaign}
                      >
                        {utmCampaignOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </Field>
                </>
              )}
          </div>
        </OverflowFrame>
      </SettingsSidebar>
    </>
  );
}
